<template>
  <div class="edit-course-container">
    <Sidebar @toggle-sidebar="toggleSidebar" :class="{ 'expanded': isSidebarOpen }" />
    
    <main class="main-panel">
      <header class="page-header">
        <div class="breadcrumb">
          <button @click="goBack" class="breadcrumb-link">
            <i class="fas fa-arrow-left"></i>
            <span>Courses</span>
          </button>
          <span class="breadcrumb-separator">/</span>
          <span class="breadcrumb-current">Edit Course</span>
        </div>
        
        <div class="header-title">
          <h1>Edit Course</h1>
          <p class="subtitle">Update course information and settings</p>
        </div>
      </header>

      <div class="content-wrapper">
        <div v-if="loading" class="loading-container">
          <div class="spinner"></div>
          <p>Loading course information...</p>
        </div>
        
        <form v-else @submit.prevent="handleUpdateCourse" class="course-form">
          <BasicInformationCard 
            v-model="courseForm"
            :instructors="normalizedInstructors"
            :categories="categories"
          />

          <CourseDescriptionCard 
            v-model="courseForm"
          />

          <MediaContentCard 
            v-model="courseForm"
          />

          <SettingsFeaturesCard 
            v-model="courseForm"
          />

          <FormActions 
            :isSubmitting="isSubmitting"
            @cancel="goBack"
          />
        </form>
      </div>
    </main>
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue';
import BasicInformationCard from '@/components/forms/BasicInformationCard.vue';
import CourseDescriptionCard from '@/components/forms/CourseDescriptionCard.vue';
import MediaContentCard from '@/components/forms/MediaContentCard.vue';
import SettingsFeaturesCard from '@/components/forms/SettingsFeaturesCard.vue';
import FormActions from '@/components/forms/FormActions.vue';
import { useCourseData } from '@/composables/useCourseData.js';

export default {
  name: 'EditCourse',
  components: {
    Sidebar,
    BasicInformationCard,
    CourseDescriptionCard,
    MediaContentCard,
    SettingsFeaturesCard,
    FormActions
  },
  setup() {
    const {
      loading,
      instructors,
      categories,
      courseForm,
      normalizedInstructors,
      fetchCategories,
      fetchInstructors,
      fetchCourse,
      updateCourse
    } = useCourseData();

    return {
      loading,
      instructors,
      categories,
      courseForm,
      normalizedInstructors,
      fetchCategories,
      fetchInstructors,
      fetchCourse,
      updateCourse
    };
  },
  data() {
    return {
      isSubmitting: false,
      isSidebarOpen: false
    };
  },
  methods: {
    toggleSidebar() {
      this.isSidebarOpen = !this.isSidebarOpen;
    },
    async handleUpdateCourse() {
      try {
        this.isSubmitting = true;
        await this.updateCourse(this.$route.params.id, this.$emit);
      } catch (error) {
        console.error('Error updating course:', error);
        this.$emit('show-notification', { 
          type: 'error', 
          message: error.message || 'Error updating course' 
        });
      } finally {
        this.isSubmitting = false;
      }
    },
    goBack() {
      this.$router.back();
    }
  },
  async created() {
    await this.fetchCategories();
    await this.fetchInstructors();
    await this.fetchCourse(this.$route.params.id);
  }
};
</script>

<style scoped>
.edit-course-container {
  display: flex;
  min-height: 100vh;
  background-color: #f8fafc;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
}

.main-panel {
  flex: 1;
  margin-left: 280px;
  transition: margin-left 0.3s ease;
  padding: 2rem;
}

/* Responsive sidebar adjustments */
.sidebar.expanded + .main-panel {
  margin-left: 280px;
}

@media (max-width: 991px) {
  .main-panel {
    margin-left: 0;
  }
  
  .sidebar.expanded + .main-panel {
    margin-left: 0;
  }
}

/* Page header styling */
.page-header {
  margin-bottom: 2rem;
}

.breadcrumb {
  display: flex;
  align-items: center;
  margin-bottom: 1.25rem;
  font-size: 0.875rem;
}

.breadcrumb-link {
  display: inline-flex;
  align-items: center;
  color: #3b82f6;
  text-decoration: none;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 0.875rem;
}

.breadcrumb-link i {
  margin-right: 0.5rem;
  font-size: 0.75rem;
}

.breadcrumb-separator {
  margin: 0 0.5rem;
  color: #94a3b8;
}

.breadcrumb-current {
  color: #64748b;
}

.header-title h1 {
  font-size: 1.875rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 0.5rem 0;
}

.subtitle {
  color: #64748b;
  font-size: 1rem;
  margin: 0;
}

/* Content area styling */
.content-wrapper {
  margin-bottom: 3rem;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(59, 130, 246, 0.2);
  border-radius: 50%;
  border-top-color: #3b82f6;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Form styling */
.course-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
</style>
 